<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import type { Map as LeafletMap } from 'leaflet';
  import { LCircle, LMap, LTileLayer } from 'vue2-leaflet';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import type { ILatLng } from '@/domain/store-setup/location-step';
  import { currencySymbol } from '@/domain/store-setup/money';
  import type { IOrdersForm } from '@/domain/store-setup/orders-setup';
  import {
    RADIUS_SEGMENTS,
    circleBounds,
    feeTextPatch,
    radiusFromSegment,
    radiusSegmentValue,
    toggleFeeChip,
  } from '@/domain/store-setup/orders-step';
  import type { TOrdersFormPatch } from '@/domain/store-setup/orders-step';
  import { OSM_TILE_ATTRIBUTION, OSM_TILE_URL } from '@/lib/osm-tiles';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Delivery» (plan §4.8 item 2): the fee (an amount, «Gratis» or «A
   * definir») and the reach (2 / 5 / 10 km or «Sin límite») over a small
   * non-interactive map of the circle around the store. Both start empty:
   * an invented fee must never be saved. A branch that already has delivery
   * zones is not asked for the reach. Shown only while delivery is on and
   * orders are on (`v-show`: the fee input is never unmounted).
   */
  const FEE_MAX_LENGTH = 14;
  /** Map zoom when no circle is drawn (street level around the store). */
  const PLAIN_ZOOM = 14;
  /** Inner margin between the circle and the map edge, in px. */
  const FIT_PADDING: [number, number] = [14, 14];
  /** orange-600: the chosen reach is an active-state indicator. */
  const CIRCLE_COLOR = '#ea580c';
  const CIRCLE_FILL_OPACITY = 0.1;
  const METERS_PER_KM = 1000;

  const props = withDefaults(
    defineProps<{
      value: IOrdersForm;
      center: ILatLng;
      currency: string;
      country: string;
      hasZones: boolean;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TOrdersFormPatch): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const feeLabelId = nextDomId('orders-fee');
  const tbdHintId = nextDomId('orders-fee-tbd');
  const reachLabelId = nextDomId('orders-reach');
  // Read once: the view is driven imperatively afterwards.
  const initialCenter: [number, number] = [props.center.lat, props.center.lng];
  const mapOptions = {
    zoomControl: false,
    dragging: false,
    touchZoom: false,
    doubleClickZoom: false,
    scrollWheelZoom: false,
    boxZoom: false,
    keyboard: false,
    tap: false,
  };
  const mapBox = ref<HTMLElement | null>(null);
  let map: LeafletMap | null = null;

  const visible = computed(() => props.value.delivery && !props.value.menuOnly);
  const symbol = computed(() => currencySymbol(props.currency));
  const chipMode = computed(
    () => props.value.feeMode === 'free' || props.value.feeMode === 'tbd'
  );
  const radiusValue = computed(() => radiusSegmentValue(props.value.radius));
  const radiusItems = computed(() =>
    RADIUS_SEGMENTS.map((segment) => ({
      id: segment.id,
      label:
        segment.km === 0
          ? tr('storeSetup.orders.noLimit')
          : tr('storeSetup.orders.km', { km: segment.km }),
    }))
  );
  const circleRadius = computed(() =>
    props.value.radius ? props.value.radius * METERS_PER_KM : 0
  );
  const circleCenter = computed<[number, number]>(() => [
    props.center.lat,
    props.center.lng,
  ]);

  //#region Fields
  function onFeeText(text: string) {
    if (!props.disabled) emit('patch', feeTextPatch(text));
  }

  function onFeeChip(mode: 'free' | 'tbd') {
    if (!props.disabled) emit('patch', toggleFeeChip(props.value, mode));
  }

  function onRadius(id: string) {
    if (!props.disabled) emit('patch', { radius: radiusFromSegment(id) });
  }
  //#endregion

  //#region Map
  /** The circle fills the map; without one, the street around the store. */
  function fit() {
    const leafletMap = map;
    if (!leafletMap) return;
    const size = leafletMap.getSize();
    // Hidden (v-show): nothing to fit until it has a size.
    if (size.x === 0 || size.y === 0) return;
    const km = props.value.radius;
    if (km)
      leafletMap.fitBounds(circleBounds(props.center, km), {
        padding: FIT_PADDING,
        animate: false,
      });
    else
      leafletMap.setView([props.center.lat, props.center.lng], PLAIN_ZOOM, {
        animate: false,
      });
  }

  /** Re-measures the map (it may have been hidden), then fits it. */
  function refresh() {
    if (!map) return;
    map.invalidateSize({ animate: false });
    fit();
  }

  function onReady(leafletMap: LeafletMap) {
    map = leafletMap;
    refresh();
  }

  watch(
    () => props.value.radius,
    () => {
      void nextTick(fit);
    }
  );
  watch(
    () => [props.center.lat, props.center.lng],
    () => {
      void nextTick(fit);
    }
  );
  // Browsers without ResizeObserver (Safari 12) still re-measure when shown.
  watch(visible, (shown) => {
    if (shown) void nextTick(refresh);
  });
  useResizeObserver(mapBox, () => refresh());
  //#endregion
</script>

<template>
  <section v-show="visible" class="v2-setup-delivery-card">
    <header class="card-head">
      <h2 class="card-title">{{ $t('storeSetup.orders.delivery') }}</h2>
      <p class="card-text">{{ $t('storeSetup.orders.deliveryHint') }}</p>
    </header>
    <div class="card-fields">
      <div class="fee-field">
        <p :id="feeLabelId" class="field-label">
          {{ $t('storeSetup.orders.fee') }}
        </p>
        <div class="fee-row">
          <PosUiInput
            class="fee-input"
            :value="value.feeText"
            :prefix="symbol"
            inputmode="decimal"
            autocomplete="off"
            :maxlength="FEE_MAX_LENGTH"
            :aria-label="$t('storeSetup.orders.feeAmount')"
            :aria-describedby="value.feeMode === 'tbd' ? tbdHintId : undefined"
            :disabled="disabled || chipMode"
            @input="onFeeText"
          />
          <div class="fee-chips" role="group" :aria-labelledby="feeLabelId">
            <PosUiChip
              :active="value.feeMode === 'free'"
              :disabled="disabled"
              @click="onFeeChip('free')"
              >{{ $t('storeSetup.orders.free') }}</PosUiChip
            >
            <PosUiChip
              :active="value.feeMode === 'tbd'"
              :disabled="disabled"
              @click="onFeeChip('tbd')"
              >{{ $t('storeSetup.orders.tbd') }}</PosUiChip
            >
          </div>
        </div>
        <p v-if="value.feeMode === 'tbd'" :id="tbdHintId" class="field-hint">
          {{ $t('storeSetup.orders.tbdHint') }}
        </p>
      </div>
      <div class="reach-field">
        <p :id="reachLabelId" class="field-label">
          {{ $t('storeSetup.orders.reach') }}
        </p>
        <p v-if="hasZones" class="custom-zones">
          {{ $t('storeSetup.orders.customZones') }}
        </p>
        <template v-else>
          <PosUiSegmented
            class="reach-segments"
            block
            :value="radiusValue"
            :items="radiusItems"
            :disabled="disabled"
            :aria-labelledby="reachLabelId"
            @input="onRadius"
          />
          <div ref="mapBox" class="reach-map">
            <LMap
              class="map-canvas"
              :center="initialCenter"
              :zoom="PLAIN_ZOOM"
              :options="mapOptions"
              @ready="onReady"
            >
              <LTileLayer
                :url="OSM_TILE_URL"
                :attribution="OSM_TILE_ATTRIBUTION"
              />
              <LCircle
                v-if="circleRadius > 0"
                :key="circleRadius"
                :lat-lng="circleCenter"
                :radius="circleRadius"
                :color="CIRCLE_COLOR"
                :fill-color="CIRCLE_COLOR"
                :fill-opacity="CIRCLE_FILL_OPACITY"
                :weight="2"
                :interactive="false"
              />
            </LMap>
            <span class="map-home" aria-hidden="true" />
            <span v-if="value.radius === 0" class="map-pill">
              {{ $t('storeSetup.orders.noLimitHint') }}
            </span>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-setup-delivery-card {
    @apply tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;
    .card-head {
      @apply tw-flex tw-flex-col tw-gap-0.5;
    }
    .card-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .card-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    // Fee beside reach where there is room; stacked on phones.
    .card-fields {
      @apply tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-gap-6;
      @media (min-width: 640px) {
        grid-template-columns: minmax(0, 13rem) minmax(0, 1fr);
      }
    }
    .fee-field,
    .reach-field {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    }
    .field-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .field-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .fee-row {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .fee-input {
      @apply tw-w-full sm:tw-max-w-[13rem];
    }
    .fee-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .custom-zones {
      @apply tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-3 tw-py-2.5 tw-text-sm tw-text-neutral-700;
    }
    .reach-segments {
      @apply tw-max-w-full;
    }
    .reach-map {
      @apply tw-relative tw-h-[180px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-100;
    }
    // Its own stacking context: Leaflet's panes (z 400–1000) stay under the overlays.
    .map-canvas {
      @apply tw-absolute tw-inset-0 tw-z-0;
      isolation: isolate;
      // Neutral like every V2 control: the admin's global link colour is the brand orange.
      :deep(.leaflet-control-attribution a) {
        @apply tw-text-neutral-600;
      }
    }
    .map-home {
      @apply tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 tw-h-3 tw-w-3 tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-bg-red-600 tw-shadow-sm;
    }
    .map-pill {
      @apply tw-absolute tw-bottom-2.5 tw-left-2.5 tw-z-10 tw-rounded-full tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-text-neutral-700;
      max-width: calc(100% - 1.25rem);
    }
  }
</style>
